/* eslint-disable no-unused-vars */
/* eslint-disable import/no-cycle */
import Sockette from 'sockette';
import PubSub from 'pubsub-js';

import {
	getAccessToken,
	hasAccessToken
} from '../../../../scanline/utils/token';
import UTIL_SERVER_WS_MESSAGES from './util.server.messages';
import UTIL_CLIENT_WS_MESSAGES from './util.client.messages';
import TopicManager from './TopicManager';

/**
 * PubSubWebsocketMetal
 *
 * A WebSocket wrapper around 'sockette' + 'pubsub-js' for:
 *  - Managing a Sockette WebSocket connection
 *  - Normalizing inbound server payloads
 *  - Sending validated & enriched outbound client messages
 *  - Handling PubSub.js topic-based subscriptions
 *
 * Main usage:
 *   const ws = new PubSubWebsocketMetal({ websocketURL });
 *   ws.subscribe('MY_TOPIC', { receiveMessage: (topic, data) => ... });
 *   ws.sendMessage({ action:'X', topic:'Y', body:{...} });
 */
class PubSubWebsocketMetal {
	/** ************************
	 *  ***********************
	 *  CONSTRUCTOR
	 *  ***********************
	 *  ********************** */

	/**
	 * @param {Object} opts
	 * @param {string} opts.websocketURL - Server WebSocket endpoint
	 * @param {Function} [opts.funcIsInternalMessage] - Function to identify internal/control messages
	 */
	constructor({
		websocketURL,
		funcIsInternalMessage = UTIL_SERVER_WS_MESSAGES.defaultFuncIsInternalMessage,
		autoPrintState = false
	} = {}) {
		if (!websocketURL) {
			throw new Error('PubSubWebsocket requires a websocketURL');
		}
		this._wsURL = websocketURL;
		this._ws = undefined;
		this._funcIsInternalMessage = funcIsInternalMessage;

		this._pendingMessageQueue = [];
		this.topicManager = new TopicManager();

		this._isInitialized = false;
		this._isInitializing = false;

		if (autoPrintState) {
			this.startAutoPrintState();
		}
	}

	/** ************************
	 *  ***********************
	 *  PRIMARY PUBLIC METHODS
	 *  ***********************
	 *  ********************** */

	/**
	 * Validate, build, and send a message via WebSocket.
	 * @param {Object} payload - Raw message payload
	 */
	sendMessage = payload => {
		UTIL_CLIENT_WS_MESSAGES.errorOnInvalidMessage(payload, {
			srcRef: 'PubSubWebsocket: sendMessage()'
		});

		const message = UTIL_CLIENT_WS_MESSAGES.createMessage(payload);
		this._sendMessage(message);
	};

	/**
	 * Subscribe to a PubSub.js topic.
	 * @param {string} topic - Topic name
	 * @param {Object} opts
	 * @param {Function} opts.receiveMessage - Callback(topic, data)
	 * @param {any} opts.owner - Owner token/id for subscription tracking
	 * @returns {{ unsubscribe: Function, token: string }}
	 */
	subscribe = (topic, { receiveMessage, owner } = {}) => {
		if (!topic || !receiveMessage) {
			throw new Error(
				'PubSubWebsocket: subscribe() requires both params "topic" & "receiveMessage"'
			);
		}
		const token = PubSub.subscribe(topic, receiveMessage); // receiveMessage (aka subscriptionHandler) format: (topic, data) => { /* do stuff */ }
		this.topicManager.joinTopic(topic, {
			token,
			subscriptionHandler: receiveMessage,
			owner
		});

		const self = this;
		const _funcUnsubscribe = () => {
			self.unsubscribe(topic, { token });
		};
		return { unsubscribe: _funcUnsubscribe, token };
	};

	/**
	 * Unsubscribe from a PubSub.js topic.
	 * Supports lookup by token, handler, or owner.
	 */
	unsubscribe = (topic, { token, receiveMessage, owner }) => {
		if (!topic) {
			throw new Error('PubSubWebsocket: unsubscribe() requires param "topic"');
		}
		if (!token && !receiveMessage && !owner) {
			throw new Error(
				'PubSubWebsocket: unsubscribe() requires either "token" OR "receiveMessage" or "owner" param'
			);
		}

		if (!token && !receiveMessage) {
			token = this.topicManager.getTopicMeta(topic, { owner })?.token;
			if (!token) {
				throw new Error(
					`PubSubWebsocket: unsubscribe() - unable to identify pubSubToken for topic: "${topic}" and owner: "${owner}"`
				);
			}
		}

		PubSub.unsubscribe(token || receiveMessage);
		this.topicManager.leaveTopic(topic, {
			token,
			subscriptionHandler: receiveMessage,
			owner
		});

		if (!this.topicManager.hasOpenTopic(topic)) {
			PubSub.unsubscribe(topic);
		}

		if (!this.topicManager.hasOpenTopics() && this.isInitialized) {
			this.topicManager.clear();
			this._destroyWebSocket();
		}
	};

	/** ************************
	 *  ***********************
	 *  INITIALIZE / DESTROY / CLEAN UP METHODS
	 *  ***********************
	 *  ********************** */

	/** Initialize the WebSocket connection (lazily on first use). */
	_initialize = () => {
		if (!this.isInitialized && !this.isInitializing) {
			this._isInitializing = true;

			const url = hasAccessToken()
				? `${this._wsURL}?auth=${getAccessToken()}`
				: this._wsURL;
			this._ws = new Sockette(url, {
				onopen: e => this._handleConnectComplete(e),
				onmessage: e => this._handleServerMessage(e)
			});
		}
	};

	/** Gracefully close and reset WebSocket connection. */
	_destroyWebSocket = () => {
		if (this._ws) {
			this._ws.close();
			this._ws = undefined;
			this._isInitialized = false;
			this._isInitializing = false;
		}
	};

	/** Unsubscribe from all topics and clear managers. */
	_unsubAll = () => {
		const topics = this.topicManager.listTopics();
		topics.forEach(topic => PubSub.unsubscribe(topic));
		this.topicManager.clear();
	};

	/** Fully tear down both subscriptions and socket. */
	destroy = () => {
		this._unsubAll();
		this._destroyWebSocket();
	};

	/** ************************
	 *  ***********************
	 *  GETTERS / SETTERS
	 *  ***********************
	 *  ********************** */

	get wsURL() {
		return this._wsURL;
	}

	get ws() {
		return this._ws;
	}

	get funcIsInternalMessage() {
		return this._funcIsInternalMessage;
	}

	get isInitialized() {
		return this._isInitialized;
	}

	get isInitializing() {
		return this._isInitializing;
	}

	/** ************************
	 *  ***********************
	 *  CORE METHODS
	 *  ***********************
	 *  ********************** */

	/** Mark initialization complete and flush queued messages. */
	_handleConnectComplete = e => {
		// console.log(e);
		this._isInitializing = false;
		this._isInitialized = true;

		this._sendPendingMessages();
	};

	/**
	 * Low-level send; queues or transmits actual message objects.
	 * @param {Object} message - Built via UTIL_CLIENT_WS_MESSAGES.createMessage()
	 */
	_sendMessage = message => {
		// assumes the message was constructued by UTIL_CLIENT_WS_MESSAGES.createMessage
		if (!message?.authParams) {
			throw new Error(
				'Error: PubSubWebsocket - _sendMessage requires a message obj, you most likely passed a message payload which needs to be run through UTIL_CLIENT_WS_MESSAGES.createMessage(payload)'
			);
		}
		if (this.isInitialized) {
			this.ws.send(JSON.stringify(message));
			// console.log('MESSAGE SENT');
			// console.log(message);
		} else {
			this._pendingMessageQueue.push(message);
			// console.log('MESSAGE QUEUED');
			// console.log(message);
			this._initialize();
		}
	};

	/**
	 * Handle server inbound message payload.
	 * - If CONNECTED: finalize connection
	 * - Else if not internal: normalize and publish via PubSub
	 */
	_handleServerMessage = payloadFromServer => {
		if (!this.funcIsInternalMessage(payloadFromServer)) {
			const message = UTIL_SERVER_WS_MESSAGES.parsePayloadFromServer(
				payloadFromServer?.data
			);
			const isError = message.error || message.isError || message.body?.isError;

			if (isError) {
				PubSub.publish(message.topic, { isError, ...message });
			} else {
				PubSub.publish(message.topic, message);
			}
		}
	};

	/** Flushes queued messages once connected. */
	_sendPendingMessages = () => {
		while (this._pendingMessageQueue.length) {
			const message = this._pendingMessageQueue.shift();
			this._sendMessage(message);
		}
	};

	/* ************************
	 * ************************
	 *  DEBUGGING
	 * ************************
	 * *********************** */

	printState() {
		const json = {
			wsURL: this._wsURL,
			hasWs: !!this._ws,
			hasPendingMessages:
				!!this._pendingMessageQueue && !!this._pendingMessageQueue.length,
			cntPendingMessages: this._pendingMessageQueue?.length,
			isInitialized: this._isInitialized,
			isInitializing: this._isInitializing,
			topicManager: this.topicManager.printState()
		};
		return json;
	}

	consoleLogState() {
		console.log('ps: ', '******************************');
		console.log('ps: ', '*****************************');
		console.log('ps: ', '******************************');
		console.log('ps: ', 'PubSubWebsocketMetal.state');
		console.log('ps: ', this.printState());
	}

	startAutoPrintState() {
		this._autoPrintState = true;
		this.consoleLogState();
		this.autoPrintState();
	}

	stopAutoPrintState() {
		this._autoPrintState = false;
	}

	autoPrintState() {
		if (!this._autoPrintState) {
			return;
		}

		setTimeout(() => {
			if (this._autoPrintState) {
				this.consoleLogState();
				this.autoPrintState();
			}
		}, 30000);
	}
}

export default PubSubWebsocketMetal;
